import { Form, Input, Table, Tooltip } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { AddIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import {
  DEFAULT_DATE_FORMAT,
  NOTE_DATE_FORMAT,
  RevxDate,
} from 'components/RevxDate';
import { TableColumnsType } from 'components/RevxTable';
import { Text } from 'components/RevxTypography';
import { ProofItem } from 'domains/workspaceDetailDomain';
import NameBadge from 'features/Workspace/components/NameBadge';
// import { useJobStatusCollection } from 'hooks/useJobStatusCollection';
import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useTranslation } from 'react-i18next';
import './index.less';

type Props = {
  choiceProof: ProofItem;
  defaultProofList: () => Promise<ProofItem[]>;
  isDeniedApprovalModal?: boolean;
  hideAction?: boolean;
  deniedApprovalHandler?: () => void;
};

export type ProofsOpenNote = {
  openNoteHandler: () => void;
};

const CommonProofNotes = forwardRef(
  (props: Props, ref: React.Ref<ProofsOpenNote>) => {
    useImperativeHandle(ref, () => ({
      openNoteHandler,
    }));
    const {
      choiceProof,
      hideAction,
      isDeniedApprovalModal,
      deniedApprovalHandler,
    } = props;
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    // const statusFilters = useJobStatusCollection();
    const [msgForm] = Form.useForm();
    const [addNoteStatus, setAddNoteStatus] = useState<boolean>(false);
    const [msgLength, setMsgLength] = useState<number>(0);

    const openNoteHandler = () => {
      setAddNoteStatus(true);
    };

    useEffect(() => {
      if (isDeniedApprovalModal) {
        setAddNoteStatus(true);
      }
    }, [isDeniedApprovalModal]);

    const onValuesChange = (
      changedValues: { msg?: string },
      allValues: { msg?: string }
    ) => {
      setMsgLength((allValues.msg || '').length);
    };

    const isLoadingNote = useAppSelector(
      (state) => state.loading.effects.workspaceDetail.proofMsg
    );

    const saveMessage = async () => {
      if (choiceProof) {
        const formData = await msgForm.validateFields();
        await dispatch.workspaceDetail.proofMsg({
          ...choiceProof.proofMsg[0],
          ...formData,
          jobProofId: choiceProof.id,
        });
        msgForm.resetFields();
        // defaultProofList();
        // dispatch.workspace.getJobCountByStatusFilter(statusFilters);
        // dispatch.workspace.reloadJobList();
      }
      if (isDeniedApprovalModal) {
        deniedApprovalHandler?.();
      }
    };

    const columns: TableColumnsType<any> = [
      {
        title: t('workspace.detail.notes.date'),
        dataIndex: 'createAt',
        key: 'id',
        render: (text) => (
          <RevxDate dateStr={text} formatStr={DEFAULT_DATE_FORMAT} />
        ),
        width: 100,
      },
      {
        title: t('workspace.detail.notes.time'),
        dataIndex: 'createAt',
        key: 'id',
        render: (text) => (
          <RevxDate dateStr={text} formatStr={NOTE_DATE_FORMAT} />
        ),
        width: 100,
      },
      {
        title: t('workspace.detail.notes.from'),
        dataIndex: 'creatorFullName',
        key: 'id',
        render: (text, record) => (
          <div className="table-tip">
            <NameBadge name={text || ''} />
            <Tooltip title={text}>
              <Text
                ellipsis
                className="table-tip-text"
                style={{
                  width: 90,
                }}
              >
                {text}
              </Text>
            </Tooltip>
          </div>
        ),
        width: 130,
      },
      {
        title: t('workspace.detail.notes.notes'),
        dataIndex: 'msg',
        key: 'id',
        render: (text, record) => (
          <div className="table-tip">
            <Tooltip title={text}>
              <Text
                ellipsis
                style={{
                  maxWidth: 650,
                }}
              >
                {text}
              </Text>
            </Tooltip>
          </div>
        ),
      },
    ];

    return (
      <>
        {/* hidden={choiceProof?.proofStatus !== ProofStatusEnum.WAITING_APPROVE} */}
        <div className="common-proof-notes-attach common-proof-notes">
          <p className="common-proof-notes-attach-title">
            {t('workspace.detail.proofs.proofNotes')}
          </p>

          <Table
            columns={columns}
            dataSource={choiceProof.proofMsg}
            pagination={false}
            size="small"
          />

          <div
            className="common-proof-notes-action"
            hidden={addNoteStatus || hideAction}
          >
            <RevxButton
              style={{ borderRadius: 50 }}
              type="primary"
              onClick={() => setAddNoteStatus(!addNoteStatus)}
            >
              <AddIcon />
              {t('workspace.detail.notes.addNote')}
            </RevxButton>
          </div>

          <div hidden={!addNoteStatus}>
            <Form form={msgForm} onValuesChange={onValuesChange}>
              <Form.Item
                label={''}
                name="msg"
                rules={[
                  () => ({
                    validator(v, z) {
                      if (!z?.trim()) {
                        return Promise.reject(
                          new Error(
                            t('workspace.detail.proofs.addMessagePlaceholder')
                          )
                        );
                      }
                      return Promise.resolve(z);
                    },
                  }),
                ]}
                className="common-proof-notes-attach-item"
              >
                <Input.TextArea
                  className="common-proof-notes-attach-input"
                  placeholder={t(
                    'workspace.detail.proofs.addMessagePlaceholder'
                  )}
                  autoSize={{ minRows: 3, maxRows: 5 }}
                  maxLength={1000}
                />
              </Form.Item>
            </Form>
            <div className="common-proof-notes-attach-action">
              <p>
                {`${t(
                  'workspace.detail.proofs.messageLength'
                )}: ${msgLength} of 1000
            ${t('workspace.detail.proofs.characters')}`}
              </p>
              <div>
                <RevxButton
                  type="ghost"
                  onClick={async () => {
                    setAddNoteStatus(!addNoteStatus);
                    msgForm.resetFields();
                  }}
                >
                  {t('common.cancel')}
                </RevxButton>
                <RevxButton
                  type="primary"
                  style={{ marginTop: 6 }}
                  loading={isLoadingNote}
                  onClick={() => saveMessage()}
                >
                  {t('workspace.detail.proofs.attachMessageButton')}
                </RevxButton>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
);

export default CommonProofNotes;
